import { c as _c } from "react/compiler-runtime";
import { ChevronDown, ChevronRight, CircleQuestionMark } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { Sidebar, SidebarContent, SidebarFooter } from '@/components/ui/sidebar';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useMeasuredKeyCount } from '@/hooks/keyboard/use-measured-key-count';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { LanguageSegmentedControl } from './LanguageSegmentedControl';
import { SidebarBrand } from './SidebarBrand';
import { SidebarFileGroup } from './SidebarFileGroup';
import { SidebarPianoCard } from './SidebarPianoCard';
import { SidebarStepItem } from './SidebarStepItem';
import { SidebarSubItem } from './SidebarSubItem';
import type { NavigationStep } from './app-navigation';
export const AppSidebar = t0 => {
  const $ = _c(48);
  const {
    steps
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    pathname
  } = useLocation();
  const {
    measured,
    total
  } = useMeasuredKeyCount();
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== measured || $[1] !== pathname || $[2] !== steps || $[3] !== t || $[4] !== total) {
    const [pianoStep, ...workflowSteps] = steps;
    T1 = Sidebar;
    t7 = "offcanvas";
    t8 = "border-r border-border";
    T0 = SidebarContent;
    t4 = "gap-6 overflow-y-auto px-3.5 pt-[26px] pb-5 short:gap-4 short:pt-4";
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SidebarBrand />;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    if ($[16] !== pianoStep) {
      t6 = pianoStep ? <SidebarPianoCard label={pianoStep.label} /> : null;
      $[16] = pianoStep;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    if ($[18] !== t) {
      t1 = t("Workflow", {
        ns: "navigation"
      });
      $[18] = t;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    t2 = "flex flex-col gap-0.5";
    t3 = workflowSteps.map(step => {
      const isActive = pathname === step.section || pathname.startsWith(`${step.section}/`);
      const hasLinks = step.links.length > 0;
      return <div key={step.section} className="flex flex-col gap-0.5"><SidebarStepItem isActive={isActive && !hasLinks} label={step.label} number={step.number ?? ""} to={step.to} trailing={hasLinks ? isActive ? <ChevronDown className="ml-auto size-3.5 text-ptl-ink-3" /> : <ChevronRight className="ml-auto size-3.5 text-ptl-ink-3" /> : <span className="ml-auto font-mono text-[11px] text-ptl-ink-3">{measured}/{total}</span>} />{isActive && hasLinks ? step.links.map(link => <SidebarSubItem key={link.to} isActive={pathname === link.to} isDisabled={link.isDisabled} label={link.label} tag={link.tag} to={link.to} />) : null}</div>;
    });
    $[0] = measured;
    $[1] = pathname;
    $[2] = steps;
    $[3] = t;
    $[4] = total;
    $[5] = T0;
    $[6] = T1;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    T0 = $[5];
    T1 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3) {
    t9 = <nav aria-label={t1} className={t2}>{t3}</nav>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SidebarFileGroup />;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== T0 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t9) {
    t11 = <T0 className={t4}>{t5}{t6}{t9}{t10}</T0>;
    $[25] = T0;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = pathname === "/help" && "font-semibold text-foreground";
  let t13;
  if ($[31] !== t12) {
    t13 = cn("inline-flex items-center gap-2 rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground", t12);
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <CircleQuestionMark className="size-[17px]" />;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t("Help", {
      ns: "navigation"
    });
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t13 || $[37] !== t15) {
    t16 = <NavLink to="/help" className={t13}>{t14}{t15}</NavLink>;
    $[36] = t13;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex items-center gap-1"><ThemeToggle /><LanguageSegmentedControl /></div>;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t16) {
    t18 = <SidebarFooter className="px-3.5 pt-0 pb-5"><div className="flex items-center justify-between gap-2 border-t border-border px-2.5 pt-3.5">{t16}{t17}</div></SidebarFooter>;
    $[40] = t16;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== T1 || $[43] !== t11 || $[44] !== t18 || $[45] !== t7 || $[46] !== t8) {
    t19 = <T1 collapsible={t7} className={t8}>{t11}{t18}</T1>;
    $[42] = T1;
    $[43] = t11;
    $[44] = t18;
    $[45] = t7;
    $[46] = t8;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  return t19;
};